import { useState, useEffect } from 'react';
import { api } from '../utils/api';

const FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'zip', 'phone'];

// StreamLaunch's own mailing address and phone number, shown near the top of
// the admin dashboard. The phone is what Company Info uses for the signing
// official, so changing it here updates every company.
const StreamLaunchContactCard = () => {
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get(`${process.env.REACT_APP_BACKEND_API_URL}/streamlaunch-settings`);
        if (res.ok) setSettings(await res.json());
      } catch (e) {
        console.error('Error loading StreamLaunch settings:', e);
      }
    };
    load();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const res = await api.put(`${process.env.REACT_APP_BACKEND_API_URL}/streamlaunch-settings`, draft);
      if (res.ok) {
        setSettings(await res.json());
        setDraft(null);
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to save');
      }
    } catch (e) {
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;

  const inputCls = 'w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500';
  const update = (key) => (e) => setDraft(prev => ({ ...prev, [key]: e.target.value }));
  const cityLine = [settings.city, [settings.state, settings.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');

  return (
    <div className="card p-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-sm font-semibold text-gray-900 mb-1">StreamLaunch Contact Info</h3>
          {!draft && (
            <div className="text-sm text-gray-700 leading-tight">
              <div>{settings.addressLine1}</div>
              {settings.addressLine2 && <div>{settings.addressLine2}</div>}
              <div>{cityLine}</div>
              <div className="mt-1">Phone: {settings.phone || 'N/A'}</div>
            </div>
          )}
        </div>
        {!draft ? (
          <button
            onClick={() => setDraft(FIELDS.reduce((acc, k) => ({ ...acc, [k]: settings[k] || '' }), {}))}
            className="px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Edit
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={() => { setDraft(null); setError(''); }}
              disabled={saving}
              className="px-3 py-1 text-sm bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </div>
      {draft && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mt-2">
          <div className="md:col-span-3">
            <label className="block text-xs text-gray-600 mb-0.5">Address Line 1</label>
            <input className={inputCls} value={draft.addressLine1} onChange={update('addressLine1')} />
          </div>
          <div className="md:col-span-3">
            <label className="block text-xs text-gray-600 mb-0.5">Address Line 2</label>
            <input className={inputCls} value={draft.addressLine2} onChange={update('addressLine2')} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-0.5">City</label>
            <input className={inputCls} value={draft.city} onChange={update('city')} />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs text-gray-600 mb-0.5">State</label>
            <input className={inputCls} value={draft.state} onChange={update('state')} />
          </div>
          <div className="md:col-span-1">
            <label className="block text-xs text-gray-600 mb-0.5">ZIP</label>
            <input className={inputCls} value={draft.zip} onChange={update('zip')} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs text-gray-600 mb-0.5">Phone</label>
            <input type="tel" className={inputCls} value={draft.phone} onChange={update('phone')} />
          </div>
          {error && <p className="md:col-span-6 text-xs text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
};

export default StreamLaunchContactCard;
